import React, {useCallback, useMemo, useState} from "react";
import {AccountingTransactionTypes} from "../../../../../../../../../core/constants/enums";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import classnames from "classnames";
import {ReactComponent as RefundIcon} from "../../../../../../../../../assets/images/credit-card-refund.svg";
import SummaryActionCard from "../../../../../../../../components/app-specific/summary-cards/summary-action-card";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import PayForOrderDialog from "../../../../../../../../components/dialogs/pay-for-order";
import {CircularProgress} from "@mui/material";
import AddCreditToOrderDialog from "../../../../../../../../components/dialogs/add-credit-to-order";
import Api from "../../../../../../../../../core/services/api/api";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {Link, useParams} from "react-router-dom";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import TransactionReceiptDialog from "../../../../../../../../components/dialogs/transaction-receipt-dialog";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import RefundTransactionsDialog from "../../../../../../../../components/dialogs/refund-transactions";
import ToastService from "../../../../../../../../../core/services/toast";
import IssueCreditFromInvoiceDialog from "../../../../../../../../components/dialogs/issue-credit-from-invoice-dialog";
import PartnerIndustryRules from "../../../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../../../core/services/cache/cache_service";


const billInfoSummaryBodyIds = {
    subTotal: 'subTotal',
    discount: 'discount',
    shippingFee: 'shippingFee',
    tax: (i) => `tax-${i}`,
    total: 'total',
    transaction: (i) => `transaction-${i}`,
}

const billInfoSummaryActionIds = {
    pay: "pay",
    credits: 'credits',
    paid: "paid",
    navigateToAccounting: "navigateToAccounting",
    refundTransactions: "refundTransactions",
    issueCredit: "issueCredit",
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const initRefundTransactionsDialogState = {open: false, close: () => void 0};

const initIssueCreditDialogState = {open: false, close: () => void 0, items: [], invoiceNo: '', customerId: undefined};

const InvoiceInformationViewBillInfoSection = ({
                                                   order,
                                                   setOrder,
                                                   loading,
                                                   removeTransaction,
                                                   payOrder,
                                                   customerId,
                                                   allowPayments,
                                               }) => {
    const params = useParams();
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const [refundTransasctionsDialogState, setRefundTransactionsDialogState] = useState(initRefundTransactionsDialogState);
    const [issueCreditDialogState, setIssueCreditDialogState] = useState(initIssueCreditDialogState);
    const [openPayDialog, setOpenPayDialog] = useState(false);
    const [openCreditsDialog, setOpenCreditsDialog] = useState(false);
    const [updatingTransactions, setUpdatingTransactions] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const partnerInformation = usePartnerInformation();

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id]);
    const forceCustomersAsCod = useMemo(() => PartnerIndustryRules.forcesCustomersAsCod(partnerId), [partnerId]);
    const isNeededToPay = useMemo(() => PartnerIndustryRules.effectiveIsNeededToPay(order, partnerId), [order, partnerId]);
    const creditMemos = useMemo(() =>
            order?.appliedTransactions?.filter(e => e.transactionTypeName === AccountingTransactionTypes.CreditMemo) ?? [],
        [order?.appliedTransactions])
    const refundableTransactions = useMemo(() =>
            order?.appliedTransactions
                ?.filter(e =>
                    e.transactionTypeName !== AccountingTransactionTypes.invoice
                    && [AccountingTransactionTypes.payment, AccountingTransactionTypes.otherPayment].includes(e.transactionTypeName)
                ) ?? [],
        [order?.appliedTransactions])

    /**
     * Adds the selected Credit to the order.
     *
     * -if the result of the api is successful, Add the transaction to transactions list of the order info.
     * @type {(function(*): Promise<void>)}
     */
    const addCreditToOrder = useCallback(async (credit) => {
        const forApi = {
            customerId: customerId,
            transactionNo: credit.transactionNo,
            invoiceId: orderId,
        }
        const response = await Api.addCreditToInvoice(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
    }, [orderId, customerId, isMounted, setOrder])

    /**
     * Updates the transactions through the given mutator.
     * @type {(function(*=, ...[*]): Promise<void>)}
     */
    const updateTransaction = useCallback(async (mutator, ...args) => {
        setUpdatingTransactions(true);
        await mutator(...args);
        if (!isMounted())
            return;
        setUpdatingTransactions(false);
    }, [isMounted])

    /**
     * Gets users confirmation before removing the current order transaction entirely.
     * @type {(function(*): void)}
     */
    const getRemoveTransactionConfirmation = useCallback(async (transaction) => {
        if (!Number.isFinite(Number(transaction?.id))) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return false;
        }
        ToastService.mainAPIRedZoneWarning();
        return new Promise(r => {
            window.partner?.openConfirmationDialog(
                <div className={'p-3'}>
                    <p className={'text-sm text-color-2'}>
                        Are you sure that you want to remove this transaction and
                        get {formatMoney(transaction?.amount ?? 0)} refunded to your account?
                    </p>
                </div>,
                {
                    maxWidth: 'sm'
                },
                async (v) => {
                    if (v) {
                        await updateTransaction(
                            removeTransaction,
                            transaction.id
                        );
                    }
                    r(v);
                },
            );
        })
    }, [removeTransaction, updateTransaction])

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    /**
     * Opens the refund transactions dialog
     * @type {(function(): void)}
     */
    const openRefundTransactionsDialog = useCallback(() => {
        setRefundTransactionsDialogState({
            open: true,
            close: () => setRefundTransactionsDialogState(initRefundTransactionsDialogState)
        });
    }, [])

    /**
     * Opens the issue credit dialog
     * @type {(function(): void)}
     */
    const openIssueCreditDialog = useCallback(() => {
        setIssueCreditDialogState({
            open: true,
            close: () => setIssueCreditDialogState(initIssueCreditDialogState),
            items: order?.items ?? [],
            invoiceNo: order?.invoiceNo,
            customerId: customerId,
        })
    }, [customerId, order?.invoiceNo, order?.items])

    const billInfoSummaryBody = useMemo(() => {
        const res = [];
        const shippingFeesAmount = (order?.freightFee ?? 0);
        const discountAmount = (order?.totalItemsDiscountAmount ?? 0);
        const transactions = order?.appliedTransactions?.filter(e => ![AccountingTransactionTypes.userOrder, AccountingTransactionTypes.invoice].includes(e.transactionTypeName)) ?? [];
        res.push({
            id: billInfoSummaryBodyIds.subTotal,
            title: 'Sub total',
            value: order?.subTotal,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        if (discountAmount > 0) {
            res.push({
                id: billInfoSummaryBodyIds.discount,
                value: -discountAmount,
                title: `Discount ${(order?.discountPercentage ?? 0) > 0 ? `(${order?.discountPercentage}%)` : ''}`,
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.shippingFee,
            value: shippingFeesAmount,
            title: `Shipping Fee`,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        let taxI = 0;
        for (const tax of order?.taxes ?? []) {
            res.push({
                id: billInfoSummaryBodyIds.tax(taxI++),
                title: <span className={classnames({'line-through': tax?.exemptNo?.length})}>
                    {tax.shortName ?? '--'} ({tax?.rate ?? '--'}%)
                </span>,
                value: tax?.exemptNo?.length ? `exempt: ${tax?.exemptNo}` : formatMoney(tax.total),
                loadingValue: "loading...",
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.total,
            title: 'Total',
            value: order?.total,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        });
        let transactionI = 0;
        for (const transaction of transactions) {
            switch (transaction?.transactionTypeName) {
                case AccountingTransactionTypes.giftCard:
                default:
                    res.push({
                        id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                        title: transaction?.transactionTypeDescription,
                        value: -(transaction?.amount ?? 0),
                        loadingValue: "loading...",
                        format: formatMoney,
                        action: {show: false},
                    })
                    break;
                case AccountingTransactionTypes.payment:
                case AccountingTransactionTypes.otherPayment: {
                    const showReceipt = transaction?.transactionTypeName === AccountingTransactionTypes.payment && transaction?.receiptId && operatorAccessLevels.packages.sales.paymentReceipts;
                    res.push({
                        id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                        transaction,
                        titleSuffix:
                            showReceipt
                                ? <IconButton
                                    color={'paper.primary'}
                                    className={'ml-1'}
                                    onPressed={() => openReceiptDialog(transaction)}
                                >
                                    <ReceiptLongRounded className={'icon normal'}/>
                                </IconButton>
                                : null,
                        title: transaction?.transactionTypeDescription,
                        value: -(transaction?.amount ?? 0),
                        loadingValue: "loading...",
                        format: formatMoney,
                        action: {
                            show: allowPayments && operatorAccessLevels.packages.sales.manageInvoices && operatorAccessLevels.packages.sales.refundInvoiceTransactions,
                            submitting: updatingTransactions,
                            icon: <RefundIcon className={'icon small'}/>,
                            submittingIcon: <CircularProgress color={'primary'} size={15}/>,
                            props: {color: 'paper.error'},
                            onClick: (entry) => getRemoveTransactionConfirmation(entry.transaction),
                        },
                    })
                    break;
                }
            }
        }
        return res;
    }, [order?.freightFee, order?.totalItemsDiscountAmount, order?.appliedTransactions, order?.subTotal, order?.total, order?.discountPercentage, order?.taxes, operatorAccessLevels.packages.sales.paymentReceipts, operatorAccessLevels.packages.sales.manageInvoices, allowPayments, updatingTransactions, openReceiptDialog, getRemoveTransactionConfirmation, operatorAccessLevels.packages.sales.refundInvoiceTransactions])

    const billInfoSummaryActions = useMemo(() => [
        {
            id: billInfoSummaryActionIds.paid,
            value: 'Invoice Paid',
            submittingValue: 'Invoice Paid',
            show: !!order && !isNeededToPay,
            disabled: true,
            variant: 'contained',
            className: 'invoice-paid-container',
            onClick: () => false,
        },
        {
            id: billInfoSummaryActionIds.pay,
            value: 'Pay this invoice',
            submittingValue: 'Submitting...',
            show: !!order && isNeededToPay && allowPayments && operatorAccessLevels.packages.sales.manageInvoices,
            disabled: loading,
            variant: 'contained',
            onClick: () => setOpenPayDialog(true),
        },
        {
            id: billInfoSummaryActionIds.credits,
            value: 'Add Credit',
            submittingValue: 'Submitting...',
            show: !!order && customerId && allowPayments && !forceCustomersAsCod && isNeededToPay && operatorAccessLevels.packages.sales.manageInvoices && operatorAccessLevels.packages.sales.customerAvailableCredits,
            disabled: loading || updatingTransactions,
            variant: 'outlined',
            onClick: () => setOpenCreditsDialog(true),
        },
        {
            id: billInfoSummaryActionIds.navigateToAccounting,
            value: 'View in Accounting Tab',
            submittingValue: 'Submitting...',
            show: !!order && !allowPayments && operatorAccessLevels.packages.sales.manageInvoices && !!customerId,
            disabled: loading,
            variant: 'contained',
            props: {
                component: Link,
                to: routeFunctions.customer.accounting.invoices.single(customerId ?? 0, orderId),
            },
        },
        {
            id: billInfoSummaryActionIds.refundTransactions,
            value: 'Refund Transactions',
            submittingValue: 'Submitting...',
            show: !!order && allowPayments && operatorAccessLevels.packages.sales.manageInvoices && !!customerId && !!refundableTransactions.length && operatorAccessLevels.packages.sales.refundInvoiceTransactions,
            disabled: loading,
            variant: 'outlined',
            onClick: () => openRefundTransactionsDialog(),
        },
        {
            id: billInfoSummaryActionIds.issueCredit,
            value: 'Issue Credit',
            submittingValue: 'Submitting...',
            show: !!order && allowPayments && operatorAccessLevels.packages.sales.manageInvoices && !!customerId && !!order?.items?.length && (order?.balance ?? 0) >= 0 && operatorAccessLevels.packages.sales.issueCredit,
            disabled: loading,
            variant: 'outlined',
            onClick: () => openIssueCreditDialog(),
        }
    ], [order, isNeededToPay, forceCustomersAsCod, allowPayments, operatorAccessLevels.packages.sales.manageInvoices, operatorAccessLevels.packages.sales.customerAvailableCredits, operatorAccessLevels.packages.sales.issueCredit, loading, customerId, updatingTransactions, orderId, refundableTransactions.length, openRefundTransactionsDialog, openIssueCreditDialog, operatorAccessLevels.packages.sales.refundInvoiceTransactions])

    const billInfoSummary = useMemo(() => ({
        header: {
            title: 'Balance',
            value: order?.balance,
            loadingValue: "Loading...",
            format: formatMoney,
        },
        body: billInfoSummaryBody,
        actions: billInfoSummaryActions,
    }), [billInfoSummaryBody, billInfoSummaryActions, order?.balance])


    return (
        <>
            <SummaryActionCard
                className={'auto-height'}
                header={billInfoSummary.header}
                body={billInfoSummary.body}
                actions={billInfoSummary.actions}
                loadingCount={billInfoSummary.body.length}
                loading={loading}
            />
            <AddCreditToOrderDialog
                customerId={customerId}
                open={openCreditsDialog}
                selectedCreditTransactionNos={creditMemos?.map(e => e.transactionNo) ?? []}
                close={() => setOpenCreditsDialog(false)}
                onAdd={addCreditToOrder}
                onRemove={(transaction) => updateTransaction(
                    removeTransaction,
                    creditMemos.find(e => e.transactionNo === transaction.transactionNo)?.id
                )}
            />
            <PayForOrderDialog
                open={openPayDialog}
                close={() => setOpenPayDialog(false)}
                pay={payOrder}
                customerId={customerId}
                remainingBalance={PartnerIndustryRules.effectiveRemainingBalance(order, partnerId)}
            />
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
            {
                operatorAccessLevels.packages.sales.refundInvoiceTransactions &&
                <RefundTransactionsDialog
                    open={refundTransasctionsDialogState.open}
                    close={refundTransasctionsDialogState.close}
                    transactions={refundableTransactions}
                    refundTransaction={getRemoveTransactionConfirmation}
                />
            }

            <IssueCreditFromInvoiceDialog
                items={issueCreditDialogState.items}
                open={issueCreditDialogState.open}
                close={issueCreditDialogState.close}
                invoiceNo={issueCreditDialogState.invoiceNo}
                customerId={issueCreditDialogState.customerId}
            />
        </>
    )
}


export default InvoiceInformationViewBillInfoSection;
